import React from 'react';

function TableRow({ cells, header }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: cells.map(() => '1fr').join(' '),
      gap: 0,
      borderBottom: '1px solid var(--border)',
      background: header ? 'var(--surface)' : 'transparent',
    }}>
      {cells.map((cell, i) => (
        <div key={i} style={{
          padding: '10px 14px',
          fontSize: header ? 11 : 13,
          fontWeight: header ? 700 : 400,
          color: header ? 'var(--text3)' : 'var(--text2)',
          textTransform: header ? 'uppercase' : 'none',
          letterSpacing: header ? '0.06em' : 'normal',
          borderRight: i < cells.length - 1 ? '1px solid var(--border)' : 'none',
        }}>
          {cell}
        </div>
      ))}
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden',
      marginTop: 12, marginBottom: 8,
    }}>
      <TableRow cells={headers} header />
      {rows.map((row, i) => (
        <TableRow key={i} cells={row} />
      ))}
    </div>
  );
}

export default function ServiceLevelAgreement({ onBack }) {
  const effectiveDate  = 'June 1, 2026';
  const supportEmail   = 'theclearaudit@gmail.com';

  const sections = [
    {
      title: '1. Uptime Commitment',
      content: `TheClearAudit commits to the following monthly uptime targets based on subscription plan:\n\nUptime is calculated as: ((Total Minutes in Month − Downtime Minutes) / Total Minutes in Month) × 100\n\nDowntime is defined as periods when the core platform (login, assessment, evidence locker, report generation) is completely unavailable. Scheduled maintenance windows (see Section 2) are excluded from downtime calculations.\n\nUptime is measured from our infrastructure monitoring systems and is available for customer review upon request. We will publish real-time and historical status at theclearaudit.com.`,
      table: {
        headers: ['Plan', 'Monthly Uptime Target', 'Maximum Monthly Downtime'],
        rows: [
          ['Professional', '99.9%', '~43 minutes'],
          ['Business',     '99.9%', '~43 minutes'],
        ],
      },
    },
    {
      title: '2. Scheduled Maintenance',
      content: `To maintain platform reliability, TheClearAudit may perform scheduled maintenance during the following standard windows:\n\n• Standard Maintenance Window: Sundays, 2:00 AM – 4:00 AM Eastern Time\n• Emergency Maintenance: Performed as needed with as much advance notice as possible (minimum 24 hours when feasible)\n\nCustomers will be notified via email at least 48 hours in advance of scheduled maintenance windows that may cause service interruption. Emergency maintenance with shorter notice will be communicated as early as possible.\n\nScheduled maintenance windows are excluded from SLA uptime calculations. We minimize the frequency of maintenance requiring service interruption and strive to perform all maintenance during low-traffic periods.`,
    },
    {
      title: '3. Support Response Times',
      content: `TheClearAudit provides customer support through the channels and with the response times listed below. Response times are measured in business hours (Monday–Friday, 9 AM–6 PM Eastern Time, excluding US federal holidays).`,
      table: {
        headers: ['Plan', 'Support Channel', 'Response Time'],
        rows: [
          ['Professional', 'Email (theclearaudit@gmail.com)', 'Within 1 business day'],
          ['Business',     'Priority email + in-app chat',       'Within 4 business hours'],
        ],
      },
      afterTable: `Response times are targets, not guarantees. Complex issues requiring engineering investigation may take longer to fully resolve. We will provide status updates throughout the resolution process.\n\nFor critical outages (P1 incidents — see Section 4), support is available outside of standard business hours through our emergency contact channel.`,
    },
    {
      title: '4. Incident Classification',
      content: `TheClearAudit classifies incidents according to their severity and impact. Target resolution times begin from the time the incident is confirmed by TheClearAudit's engineering team.`,
      table: {
        headers: ['Priority', 'Description', 'Examples', 'Target Resolution'],
        rows: [
          ['P1 — Critical', 'Complete service outage', 'Platform inaccessible, login broken for all users', '2 hours'],
          ['P2 — High', 'Major feature unavailable', 'Assessment saving broken, PDF export failing, evidence upload unavailable', '8 hours'],
          ['P3 — Medium', 'Degraded performance or partial feature failure', 'Slow load times, intermittent errors, non-critical feature broken', '24 hours'],
          ['P4 — Low', 'Minor issue or cosmetic bug', 'Display issue, non-blocking UI bug, documentation error', '5 business days'],
        ],
      },
      afterTable: `Resolution time targets represent the time to restore service functionality, not necessarily the time to identify the root cause. Post-incident reports will be provided for P1 and P2 incidents within 5 business days of resolution.`,
    },
    {
      title: '5. SLA Credits',
      content: `If TheClearAudit fails to meet the monthly uptime commitment for your plan, you are eligible for a service credit as compensation.\n\nCredit Calculation:\n• 10% of your monthly subscription fee for each 1% (or fraction thereof) below the uptime commitment, up to a maximum credit of 30% of your monthly subscription fee.\n• Credits are applied to your next invoice. Credits are not redeemable for cash.\n\nExample: If you are on the Professional plan ($399/month) and uptime is 99.6% against a 99.9% commitment, the shortfall is 0.3%. The credit would be 3 × 10% × $399 = $119.70 applied to your next invoice.\n\nCredit Request Process:\n• You must submit a credit request to ${supportEmail} within 30 days of the incident causing the SLA shortfall.\n• Include the dates and times of the outage and the affected features.\n• TheClearAudit will review the request against our monitoring records and respond within 5 business days.\n• Credits will be applied to the next invoice following approval.\n\nMaximum Liability:\nSLA credits are your sole and exclusive remedy for service availability failures. Total credits in any calendar month will not exceed 30% of your monthly subscription fee for that month.`,
    },
    {
      title: '6. Exclusions',
      content: `The following are excluded from SLA calculations and do not qualify for SLA credits:\n\n• Scheduled maintenance windows announced at least 48 hours in advance\n• Force majeure events: natural disasters, war, civil unrest, government actions, pandemics, or other events beyond TheClearAudit's reasonable control\n• Customer-caused issues: service disruption resulting from your actions, configurations, or misuse of the platform (including API abuse, unauthorized scripts, or exceeding usage limits)\n• Internet and network outages outside TheClearAudit's control, including outages at your ISP or between your location and our infrastructure\n• Third-party service outages: temporary unavailability of third-party services that TheClearAudit depends on but does not control, including Stripe (payment processing), Supabase (database infrastructure), Vercel (hosting), and Anthropic (AI services)\n• Issues caused by browser incompatibility, client-side software, or your organization's network security controls\n• Free trials and accounts that are not on a paid subscription plan`,
    },
    {
      title: '7. Monitoring and Status',
      content: `TheClearAudit maintains continuous infrastructure monitoring to detect and respond to service issues as quickly as possible.\n\nIncident Communication: When a P1 or P2 incident occurs:\n1. Status updates will be posted at theclearaudit.com within 30 minutes of confirmation\n2. Updates will be posted at least every 2 hours during active incidents\n3. A resolution notice will be posted when service is restored\n4. All affected customers will receive direct email notification for P1 and P2 incidents\n\nIncident communications will be sent via email to all affected customers.`,
    },
  ];

  return (
    <div style={{
      height: '100vh', background: 'var(--bg)',
      fontFamily: 'var(--font)', color: 'var(--text)',
      overflowY: 'auto',
    }}>
      {/* Header */}
      <div style={{
        background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
        padding: '0 40px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7,
            background: 'linear-gradient(135deg, #2D7DD2, #1A4E8A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: '#fff',
          }}>CM</div>
          <span style={{ fontSize: 15, fontWeight: 600 }}>TheClearAudit</span>
        </div>
        {onBack && (
          <span
            onClick={onBack}
            style={{ fontSize: 13, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Back
          </span>
        )}
      </div>

      {/* Content */}
      <div style={{ maxWidth: 860, margin: '0 auto', padding: '48px 40px 80px' }}>

        {/* Title */}
        <div style={{ marginBottom: 40 }}>
          <h1 style={{
            fontSize: 32, fontWeight: 700, color: 'var(--text)',
            marginBottom: 10, letterSpacing: '-0.01em',
          }}>
            Service Level Agreement
          </h1>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 4 }}>
            Effective date: {effectiveDate}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            Applies to all paid TheClearAudit subscription plans
          </div>
        </div>

        {/* Quick ref */}
        <div style={{
          background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.25)',
          borderRadius: 10, padding: 20, marginBottom: 32,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
            At a Glance
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' }}>
            {[
              ['Professional / Business Uptime', '99.9% monthly'],
              ['Maintenance Window', 'Sundays 2–4 AM ET'],
              ['P1 Resolution Target', '2 hours'],
              ['Breach Notification', 'Within 72 hours'],
              ['Credit Max', '30% of monthly fee'],
              ['Status Updates', 'theclearaudit.com'],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
                <span style={{ color: 'var(--text3)' }}>{k}</span>
                <span style={{ color: 'var(--text)', fontWeight: 500, textAlign: 'right' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Sections */}
        {sections.map((section, i) => (
          <div key={i} style={{ marginBottom: 32 }}>
            <h2 style={{
              fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12,
            }}>
              {section.title}
            </h2>
            {section.content && (
              <div style={{
                fontSize: 13, color: 'var(--text2)', lineHeight: 1.8, whiteSpace: 'pre-line',
                marginBottom: section.table ? 8 : 0,
              }}>
                {section.content}
              </div>
            )}
            {section.table && (
              <Table headers={section.table.headers} rows={section.table.rows} />
            )}
            {section.afterTable && (
              <div style={{
                fontSize: 13, color: 'var(--text2)', lineHeight: 1.8, whiteSpace: 'pre-line',
                marginTop: 12,
              }}>
                {section.afterTable}
              </div>
            )}
            {i < sections.length - 1 && (
              <div style={{ marginTop: 32, borderBottom: '1px solid var(--border)' }} />
            )}
          </div>
        ))}

        {/* Footer */}
        <div style={{
          marginTop: 48, padding: '20px 24px',
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, textAlign: 'center',
          fontSize: 12, color: 'var(--text3)', lineHeight: 1.6,
        }}>
          Questions about this SLA? Contact{' '}
          <span style={{ color: 'var(--accent)' }}>{supportEmail}</span>
        </div>
      </div>
    </div>
  );
}
